"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { useAddToCartSliderStore } from "@/app/store/cart/add-to-cart-slider-store";
import {
	AddToCartSlider,
	AddToCartSliderContent,
	CartSubtotal
} from "@/composites/cart";
import { PromotionalProgressBarElement } from "@/app/components/shared/promotional-progress-bar-element";
import { RecommendedProductsElement } from "@/app/components/shared/recommended-products-element";
import type { Locale } from "@/domain";

export function AddToCartSliderRoot(): JSX.Element | null {
	const store = useAddToCartSliderStore();
	const locale = useLocale();
	let router: ReturnType<typeof useRouter> | null = null;

	try {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		router = useRouter();
	} catch (e) {
		// Storybook / non-Next.js context fallback
	}

	const handleClose = (): void => {
		store.close();
	};

	const handleContinueShopping = (): void => {
		store.close();
	};

	const handleViewCartAndCheckout = (): void => {
		store.close();
		if (router) {
			router.push("/cart");
		} else {
			window.location.href = "/cart";
		}
	};

	if (!store.addedProduct) {
		return null;
	}

	return (
		<AddToCartSlider
			isOpen={store.isOpen}
			onClose={handleClose}
			onContinueShopping={handleContinueShopping}
			onViewCartAndCheckout={handleViewCartAndCheckout}
			footer={
				<React.Fragment key={store.addedProduct.sku}>
					<PromotionalProgressBarElement locale={locale as Locale} />
					<CartSubtotal locale={locale} />
				</React.Fragment>
			}
		>
			<AddToCartSliderContent addedProduct={store.addedProduct} />

			<RecommendedProductsElement
				key={store.addedProduct.sku}
				sku={store.addedProduct.sku}
				locale={locale as Locale}
				source="ATC"
				variant="compact"
				title="Frequently bought together"
			/>
		</AddToCartSlider>
	);
}
